import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
} from "@nexus/ui";
import type { PostFlightOpportunity } from "@/features/post-flight/api/post-flight.api";
import {
  POST_FLIGHT_AD_TYPES,
  POST_FLIGHT_WINDOWS,
  adsMatchingFilters,
  prettyLabel,
  type PostFlightAd,
  type PostFlightAdType,
  type PostFlightWindow,
} from "@/features/post-flight/lib/post-flight-list";

const PLATFORM_CATALOG = ["meta", "tiktok", "google"] as const;
const OPPORTUNITY_TYPES = [
  "winning_creative",
  "underexplored_winner",
  "emerging_creative",
  "strong_hook",
] as const;

const TRIGGER_CLASS =
  "inline-flex h-11 min-h-11 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-[13px] text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 [@media(hover:hover)_and_(pointer:fine)]:h-[34px] [@media(hover:hover)_and_(pointer:fine)]:min-h-0";

interface PostFlightFiltersProps {
  platform: string;
  onPlatformChange: (platform: string) => void;
  adTypes: PostFlightAdType[];
  onAdTypesChange: (adTypes: PostFlightAdType[]) => void;
  types: string[];
  onTypesChange: (types: string[]) => void;
  windowDays: PostFlightWindow;
  onWindowChange: (window: PostFlightWindow) => void;
  opportunities: readonly PostFlightOpportunity[];
  ads: readonly PostFlightAd[];
}

export function PostFlightFilters({
  platform,
  onPlatformChange,
  adTypes,
  onAdTypesChange,
  types,
  onTypesChange,
  windowDays,
  onWindowChange,
  opportunities,
  ads,
}: PostFlightFiltersProps) {
  const { t } = useTranslation();
  const connected = new Set(
    opportunities.map((row) => row.source.trim().toLowerCase()).filter((source) => source.length > 0),
  );
  const platforms = [
    ...PLATFORM_CATALOG,
    ...[...connected].filter((source) => !PLATFORM_CATALOG.includes(source as (typeof PLATFORM_CATALOG)[number])),
  ];
  const platformLabel = platform
    ? t(`postFlight.platforms.${platform}`, prettyLabel(platform))
    : t("postFlight.filters.all", "All");
  const adTypeLabel =
    adTypes.length === 0
      ? t("postFlight.filters.all", "All")
      : adTypes.length === 1
        ? t(`postFlight.card.${adTypes[0]}`, adTypes[0] ?? "")
        : t("postFlight.filters.selected", "{{count}} selected", { count: adTypes.length });
  const typeLabel =
    types.length === 0
      ? t("postFlight.filters.all", "All")
      : types.length === 1
        ? t(`postFlight.types.${types[0]}`, types[0] ?? "")
        : t("postFlight.filters.selected", "{{count}} selected", { count: types.length });

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <DropdownMenu>
          <DropdownMenuTrigger className={TRIGGER_CLASS} aria-label={t("postFlight.filters.platform", "Platforms")}>
            <span className="text-muted-foreground">{t("postFlight.filters.platform", "Platforms")}:</span>
            <span className="font-semibold">{platformLabel}</span>
            <Icon name="chevron-down" className="size-3.5 text-muted-foreground" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[220px]">
            <DropdownMenuItem onSelect={() => onPlatformChange("")} className="gap-2">
              <span>{t("postFlight.filters.all", "All")}</span>
              {platform === "" ? <Icon name="check" className="ml-auto size-3.5 text-primary" /> : null}
            </DropdownMenuItem>
            {platforms.map((id) => {
              const isConnected = connected.has(id);
              const isSelected = platform === id;
              return (
                <DropdownMenuItem
                  key={id}
                  disabled={!isConnected}
                  onSelect={() => {
                    if (isConnected) onPlatformChange(id);
                  }}
                  className="gap-2"
                >
                  <span>{t(`postFlight.platforms.${id}`, prettyLabel(id))}</span>
                  {isConnected ? null : (
                    <span className="text-[12px] text-muted-foreground">
                      {t("postFlight.filters.notConnected", "Not connected")}
                    </span>
                  )}
                  {isSelected ? <Icon name="check" className="ml-auto size-3.5 text-primary" /> : null}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger className={TRIGGER_CLASS} aria-label={t("postFlight.filters.adType", "Ad type")}>
            <span className="text-muted-foreground">{t("postFlight.filters.adType", "Ad type")}:</span>
            <span className="font-semibold">{adTypeLabel}</span>
            <Icon name="chevron-down" className="size-3.5 text-muted-foreground" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[220px]">
            {POST_FLIGHT_AD_TYPES.map((id) => {
              const selected = adTypes.includes(id);
              const count = adsMatchingFilters(ads, types, platform, [id]).length;
              return (
                <DropdownMenuItem
                  key={id}
                  onSelect={(event) => {
                    event.preventDefault();
                    onAdTypesChange(selected ? adTypes.filter((value) => value !== id) : [...adTypes, id]);
                  }}
                >
                  <span
                    className={`flex size-4 shrink-0 items-center justify-center rounded-sm border ${
                      selected ? "border-primary bg-primary text-primary-foreground" : "border-input-border bg-card"
                    }`}
                  >
                    {selected ? <Icon name="check" className="size-3 text-primary-foreground" /> : null}
                  </span>
                  <span className="flex-1">{t(`postFlight.card.${id}`, id)}</span>
                  <span className="text-[12px] text-muted-foreground tabular-nums">{count}</span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger className={TRIGGER_CLASS} aria-label={t("postFlight.filters.type", "Opportunity type")}>
            <span className="text-muted-foreground">{t("postFlight.filters.type", "Opportunity type")}:</span>
            <span className="font-semibold">{typeLabel}</span>
            <Icon name="chevron-down" className="size-3.5 text-muted-foreground" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-[260px]">
            {OPPORTUNITY_TYPES.map((id) => {
              const selected = types.includes(id);
              const count = adsMatchingFilters(ads, [id], platform, adTypes).length;
              return (
                <DropdownMenuItem
                  key={id}
                  onSelect={(event) => {
                    event.preventDefault();
                    onTypesChange(selected ? types.filter((value) => value !== id) : [...types, id]);
                  }}
                >
                  <span
                    className={`flex size-4 shrink-0 items-center justify-center rounded-sm border ${
                      selected ? "border-primary bg-primary text-primary-foreground" : "border-input-border bg-card"
                    }`}
                  >
                    {selected ? <Icon name="check" className="size-3 text-primary-foreground" /> : null}
                  </span>
                  <span className="flex-1">{t(`postFlight.types.${id}`, id)}</span>
                  <span className="text-[12px] text-muted-foreground tabular-nums">{count}</span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="ml-auto flex items-center">
        <DropdownMenu>
          <DropdownMenuTrigger className={TRIGGER_CLASS} aria-label={t("postFlight.window.group", "Date window")}>
            <Icon name="calendar" className="size-3.5 text-muted-foreground" />
            <span className="font-semibold">{t("postFlight.window.last", "Last {{count}} days", { count: windowDays })}</span>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="min-w-[180px]">
            {POST_FLIGHT_WINDOWS.map((days) => (
              <DropdownMenuItem key={days} onSelect={() => onWindowChange(days)}>
                <span>{t("postFlight.window.last", "Last {{count}} days", { count: days })}</span>
                {days === windowDays ? <Icon name="check" className="ml-auto size-3.5 text-primary" /> : null}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
}
